import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaCheckCircle, FaFileInvoiceDollar, FaMoneyBillWave, FaFileUpload, FaCloudUploadAlt, FaExclamationTriangle, FaSearch, FaFilter, FaEye, FaEdit, FaTrash, FaReceipt } from "react-icons/fa";
import { 
    obtenerCuentasCobrarPorEmpresa, 
    registrarComprobanteVenta, 
    eliminarComprobanteVenta,
    actualizarComprobanteVenta,
    anularComprobanteVentaConNC
} from "../../../services/contabilidad/ventasService";
import { obtenerAsientoPorId } from "../../../services/contabilidad/asientosService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import UploadFacturaExtractor from "./UploadFacturaExtractor";

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const aMonto = (v) => toNum(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const txt = (v) => String(v || "").trim().toUpperCase();
const normDesc = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
const normDescComparable = (v) =>
  normDesc(v)
    .replace(/�/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ES_LINEA_NO_OPERATIVA = /(\bigv\b|\bisc\b|\bicbper\b|\bimpuesto\b|\bredondeo\b|\bdescuento\b|\bretenci[oó]n\b|\bpercepci[oó]n\b|\bdetracci[oó]n\b|\banticipo\b|\bcargo\b|\bajuste\b)/i;

const esLineaFacturaIgnorable = (linea = {}) => {
  const descripcion = String(linea?.descripcion || "").trim();
  const descripcionNorm = normDesc(descripcion);
  const cantidad = toNum(linea?.cantidad);
  const pu = toNum(linea?.precioUnitario);
  const importe = Math.abs(toNum(linea?.importe ?? (cantidad * pu)));

  if (!descripcionNorm && importe <= 0.05) return true;
  if (ES_LINEA_NO_OPERATIVA.test(descripcionNorm)) return true;
  if ((cantidad <= 0 || pu <= 0) && importe <= 0.05) return true;
  return false;
};

const claveComprobante = ({ clienteRuc, tipoComprobante, serie, numero }) => {
  const ruc = txt(clienteRuc);
  const tipo = txt(tipoComprobante);
  const s = txt(serie);
  const n = txt(numero);
  if (!ruc || !tipo || !s || !n) return "";
  return `${ruc}|${tipo}|${s}|${n}`;
};

const amarrarLineasFacturaConOs = (lineasFactura = [], lineasOs = []) => {
  const pendientes = (lineasOs || []).map((l) => ({
    ...l,
    cantidad_pendiente_local: toNum(l.cantidad_pendiente ?? l.cantidad ?? 0),
  }));

  const lineasAmarradas = [];
  let sinMatch = 0;

  (lineasFactura || []).forEach((lf) => {
    const cant = toNum(lf?.cantidad);
    const pu = toNum(lf?.precioUnitario);
    const desc = normDescComparable(lf?.descripcion);

    if (esLineaFacturaIgnorable(lf)) {
      return;
    }

    if (cant <= 0 || pu <= 0) {
      sinMatch += 1;
      return;
    }

    const candidatos = pendientes.filter((o) => toNum(o.cantidad_pendiente_local) > 0);
    if (candidatos.length === 0) {
      sinMatch += 1;
      return;
    }

    let target = candidatos.find((o) => {
      const d = normDescComparable(o.descripcion);
      const puOs = toNum(o.precio_unitario);
      return d === desc && Math.abs(puOs - pu) < 0.01;
    });

    if (!target) {
      target = candidatos.find((o) => {
        const d = normDescComparable(o.descripcion);
        const puOs = toNum(o.precio_unitario);
        const descripcionCompatible = d.includes(desc) || desc.includes(d);
        return descripcionCompatible && Math.abs(puOs - pu) < 0.01;
      });
    }

    if (!target) {
      target = candidatos.find((o) => {
        const d = normDescComparable(o.descripcion);
        const descripcionCompatible = d.includes(desc) || desc.includes(d);
        const cantOs = toNum(o.cantidad_pendiente_local);
        return descripcionCompatible && Math.abs(cantOs - cant) < 0.01;
      }) || null;
    }

    if (!target) {
      target = candidatos.find((o) => Math.abs(toNum(o.precio_unitario) - pu) < 0.01) || null;
    }

    if (!target) {
      const importeLinea = Math.abs(toNum(lf?.importe ?? (cant * pu)));
      target = candidatos.find((o) => {
        const puOs = toNum(o.precio_unitario);
        const cantDisponible = toNum(o.cantidad_pendiente_local);
        const importeOs = Math.abs(toNum(puOs * Math.min(cant, cantDisponible)));
        return Math.abs(importeOs - importeLinea) < 0.05;
      }) || null;
    }

    // Fallbacks si no se encontró por coincidencia exacta de precio o importe:
    // 1. Buscar coincidencia exacta por descripción
    if (!target) {
      target = candidatos.find((o) => {
        const d = normDescComparable(o.descripcion);
        return d === desc;
      }) || null;
    }

    // 2. Buscar coincidencia por descripción compatible (contiene)
    if (!target) {
      target = candidatos.find((o) => {
        const d = normDescComparable(o.descripcion);
        return d.includes(desc) || desc.includes(d);
      }) || null;
    }

    // 3. Fallback final: Emparejar con el primer candidato disponible
    if (!target && candidatos.length > 0) {
      target = candidatos[0];
    }

    if (!target) {
      sinMatch += 1;
      return;
    }

    const cantidadAmarrada = Math.min(cant, toNum(target.cantidad_pendiente_local));
    if (cantidadAmarrada <= 0) {
      sinMatch += 1;
      return;
    }

    const importeOriginalLinea = toNum(lf?.importe);
    const importe =
      importeOriginalLinea > 0 && cant > 0
        ? toNum((cantidadAmarrada / cant) * importeOriginalLinea)
        : toNum(cantidadAmarrada * pu);
    target.cantidad_pendiente_local = toNum(target.cantidad_pendiente_local - cantidadAmarrada);

    lineasAmarradas.push({
      descripcion: lf.descripcion,
      cantidad: cantidadAmarrada,
      precioUnitario: pu,
      importe,
      lineaOsId: target.id,
      indiceOs: Number.isFinite(Number(target.indice)) ? Number(target.indice) : null,
      precioUnitarioOs: toNum(target.precio_unitario),
    });
  });

  return { lineasAmarradas, sinMatch };
};

const GestionCuentasCobrar = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || usuario?.rucEmpresa || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [cxc, setCxc] = useState([]);
  const [savingFactura, setSavingFactura] = useState(false);
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalAsiento, setModalAsiento] = useState({ visible: false, data: null, titulo: "" });
  const [loadingAsiento, setLoadingAsiento] = useState(false);
  const [busquedaHistorial, setBusquedaHistorial] = useState("");
  const [filtroHistorial, setFiltroHistorial] = useState("todos");
  const [osSeleccionadaId, setOsSeleccionadaId] = useState("");
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    serie: "",
    numero: "",
    fechaEmision: "",
    fechaVencimiento: "",
    fechaCorte: "",
    ordenVentaId: ""
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Estado para Modal Vincular Nota de Crédito (NC)
  const [showNcModal, setShowNcModal] = useState(false);
  const [ncForm, setNcForm] = useState({
    comprobanteId: "",
    comprobanteTexto: "",
    serieNc: "FC01",
    numeroNc: "",
    motivo: "Anulación por error en emisión de comprobante"
  });
  const [savingNc, setSavingNc] = useState(false);

  const recargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      setError("");
      const data = await obtenerCuentasCobrarPorEmpresa(rucEmpresa);
      setCxc(data || []);
    } catch (e) {
      setError("No se pudo cargar cuentas por cobrar.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => { recargar(); }, [recargar]);

  const resumen = useMemo(() => ({
    pendiente: cxc.reduce((acc, x) => acc + toNum(x.saldo_pendiente), 0),
    cobrado: cxc.reduce((acc, x) => acc + toNum(x.total_cobrado), 0),
    facturado: cxc.reduce((acc, x) => acc + toNum(x.total_facturado), 0),
  }), [cxc]);

  const facturasHistorial = useMemo(() => {
    return (cxc || []).flatMap((x) =>
      (x.comprobantes_relacionados || []).map((c) => ({
        id: `${x.id}_${c.id || `${c?.serie || ""}-${c?.numero || ""}`}`,
        osId: x.id,
        osCorrelativo: x.os_correlativo || "-",
        clienteRazonSocial: x.cliente_razon_social || "-",
        clienteRuc: x.cliente_ruc || "-",
        moneda: x.moneda || "PEN",
        tipo: c?.tipo || c?.tipo_comprobante || "FACTURA",
        serie: c?.serie || "",
        numero: c?.numero || "",
        fechaEmision: c?.fecha_emision || "",
        total: toNum(c?.total),
        estadoPago: txt(c?.estado_pago || (c?.anulado || c?.tiene_nc ? "CANCELADO_NC" : "PENDIENTE")),
        notaCreditoRef: c?.nota_credito_referencia || c?.nota_credito_id || "",
        asientoId: c?.asiento_id || "",
        comprobanteId: c?.id || "",
      }))
    );
  }, [cxc]);

  const facturasFiltradas = useMemo(() => {
    let rows = [...facturasHistorial];

    if (busquedaHistorial.trim()) {
      const term = busquedaHistorial.trim().toLowerCase();
      rows = rows.filter((x) => {
        return (
          String(x?.clienteRazonSocial || "").toLowerCase().includes(term) ||
          String(x?.clienteRuc || "").toLowerCase().includes(term) ||
          String(x?.osCorrelativo || "").toLowerCase().includes(term) ||
          `${x?.tipo || ""} ${x?.serie || ""}-${x?.numero || ""}`.toLowerCase().includes(term)
        );
      });
    }

    if (filtroHistorial === "cobrado") {
      rows = rows.filter((x) => x.estadoPago === "PAGADO");
    } else if (filtroHistorial === "pendiente") {
      rows = rows.filter((x) => x.estadoPago !== "PAGADO");
    } else if (filtroHistorial === "conAsiento") {
      rows = rows.filter((x) => !!x.asientoId);
    }

    return rows;
  }, [facturasHistorial, busquedaHistorial, filtroHistorial]);

  const osDisponiblesParaFacturar = useMemo(
    () => (cxc || []).filter((x) => toNum(x?.saldo_por_facturar) > 0.01),
    [cxc]
  );

  const clavesRegistradasEnSistema = useMemo(() => {
    const set = new Set();
    (cxc || []).forEach((x) => {
      (x.comprobantes_relacionados || []).forEach((c) => {
        const key = claveComprobante({
          clienteRuc: c?.cliente_ruc || x?.cliente_ruc || "",
          tipoComprobante: c?.tipo || c?.tipo_comprobante || "",
          serie: c?.serie || "",
          numero: c?.numero || "",
        });
        if (key) set.add(key);
      });
    });
    return set;
  }, [cxc]);

  const handleFacturasExtraidas = async (resultados) => {
    const lista = Array.isArray(resultados) ? resultados : [resultados];

    // Detectar si entre las facturas extraídas hay alguna Nota de Crédito (NC)
    for (const factura of lista) {
      const tipo = String(factura?.comprobante?.tipo || "").toUpperCase();
      const serie = String(factura?.comprobante?.serie || "").toUpperCase();
      const esNc = tipo === "NOTA_CREDITO" || tipo === "07" || serie.startsWith("FC") || serie.startsWith("EC");

      if (esNc) {
        const docModificado = txt(factura.comprobante?.documentoModificado);
        const numeroNc = txt(factura.comprobante?.numero);
        const motivoExtraido = txt(factura.comprobante?.motivo) || "Anulación por Nota de Crédito cargada";

        // Buscar comprobante objetivo por correlativo modificado
        let targetComp = null;
        if (docModificado) {
          targetComp = facturasHistorial.find((x) => {
            const fullRef = `${x.serie}-${x.numero}`.toUpperCase();
            return fullRef === docModificado || x.numero === docModificado;
          });
        }

        if (targetComp) {
          try {
            await anularComprobanteVentaConNC({
              rucEmpresa,
              comprobanteId: targetComp.comprobanteId,
              serieNc: serie || "FC01",
              numeroNc: numeroNc,
              motivo: motivoExtraido,
              usuarioRegistro: usuario?.correo || "sistema"
            });
            setOk(`Nota de Crédito ${serie}-${numeroNc} (${motivoExtraido}) cargada y vinculada automáticamente a la factura ${targetComp.serie}-${targetComp.numero}. El cliente fue desbloqueado.`);
            setShowUploadModal(false);
            await recargar();
            return;
          } catch (e) {
            setError(`Error vinculando NC cargada: ${e.message}`);
            return;
          }
        } else {
          // Si no se encontró por coincidencia de texto, abrir modal manual prellenado
          handleAbrirModalNc({
            comprobanteId: "",
            tipo: "FACTURA",
            serie: docModificado.split("-")[0] || "",
            numero: docModificado.split("-")[1] || docModificado,
            clienteRazonSocial: factura.cliente?.razonSocial || factura.proveedor?.razonSocial || "Seleccionar comprobante"
          });
          setNcForm((prev) => ({
            ...prev,
            serieNc: serie,
            numeroNc: numeroNc,
            motivo: motivoExtraido
          }));
          setShowUploadModal(false);
          setOk("Seleccione el comprobante a anular para vincular la Nota de Crédito cargada.");
          return;
        }
      }
    }

    const osSeleccionada = (cxc || []).find((x) => x.id === osSeleccionadaId);
    if (!osSeleccionada) {
      setError("Primero selecciona una OS para adjuntar facturas.");
      return;
    }

    const nuevasFacturas = [];
    let noAmarradas = 0;

    lista.forEach((factura) => {
      const lineasFactura = (factura.items || []).map(item => ({
        descripcion: item.descripcion,
        cantidad: toNum(item.cantidad),
        precioUnitario: toNum(item.precioUnitario),
        importe: toNum(item.subtotal ?? item.total)
      }));

      const { lineasAmarradas, sinMatch } = amarrarLineasFacturaConOs(lineasFactura, osSeleccionada.lineas_os || []);
      if (lineasAmarradas.length === 0 || sinMatch > 0) {
        noAmarradas += 1;
        return;
      }

      nuevasFacturas.push({
        id: Math.random().toString(36).substr(2, 9),
        ordenVentaId: osSeleccionada.id,
        osCorrelativo: osSeleccionada.os_correlativo,
        clienteRuc: osSeleccionada.cliente_ruc || factura.cliente?.ruc || factura.proveedor?.ruc || "",
        clienteRazonSocial: osSeleccionada.cliente_razon_social || factura.cliente?.razonSocial || factura.proveedor?.razonSocial || "",
        tipoComprobante: factura.comprobante?.tipo || "FACTURA",
        serie: factura.comprobante?.serie || "",
        numero: factura.comprobante?.numero || "",
        fechaEmision: factura.comprobante?.fechaEmision || "",
        fechaVencimiento: factura.comprobante?.fechaVencimiento || factura.comprobante?.fechaEmision || "",
        moneda: factura.comprobante?.moneda || "PEN",
        subtotal: factura.montos?.subtotal || 0,
        igv: factura.montos?.igv || 0,
        total: factura.montos?.total || 0,
        fechaCorte: factura.comprobante?.fechaVencimiento || factura.comprobante?.fechaEmision || "",
        lineas: lineasAmarradas,
        estado: "PENDIENTE"
      });
    });

    const clavesEnCola = new Set(
      facturasPendientes
        .map((f) => claveComprobante(f))
        .filter(Boolean)
    );
    const clavesLote = new Set();
    const aceptadas = [];
    let duplicadas = 0;

    for (const f of nuevasFacturas) {
      const key = claveComprobante(f);
      if (!key) {
        aceptadas.push(f);
        continue;
      }

      if (clavesRegistradasEnSistema.has(key) || clavesEnCola.has(key) || clavesLote.has(key)) {
        duplicadas += 1;
        continue;
      }

      clavesLote.add(key);
      aceptadas.push(f);
    }

    if (aceptadas.length > 0) {
      setFacturasPendientes(prev => [...prev, ...aceptadas]);
      setShowUploadModal(false);
      setOk(`${aceptadas.length} comprobante(s) añadido(s) a la cola.`);
    }

    if (duplicadas > 0) {
      setError(`Se omitieron ${duplicadas} comprobante(s) duplicado(s).`);
    }

    if (noAmarradas > 0) {
      setError((prev) => {
        const base = prev ? `${prev} ` : "";
        return `${base}Se omitieron ${noAmarradas} comprobante(s) porque no se pudieron amarrar todos sus items a la OS seleccionada.`;
      });
    }
  };

  const eliminarDeCola = (id) => {
    setFacturasPendientes(prev => prev.filter(f => f.id !== id));
  };

  const registrarTodoMasivo = async () => {
    if (facturasPendientes.length === 0) return;
    try {
      setSavingFactura(true);
      setError("");
      let completadosIds = [];
      let duplicadosDetectados = 0;
      const clavesRegistradasEnProceso = new Set();

      for (let i = 0; i < facturasPendientes.length; i++) {
        const f = facturasPendientes[i];
        if (f.estado === "COMPLETADO") continue;

        const key = claveComprobante(f);
        if (key && (clavesRegistradasEnSistema.has(key) || clavesRegistradasEnProceso.has(key))) {
          duplicadosDetectados += 1;
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "DUPLICADO" } : item));
          continue;
        }

        try {
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "PROCESANDO" } : item));

          if (!f.ordenVentaId) {
            throw new Error("Factura sin OS asignada.");
          }

          await registrarComprobanteVenta({
            rucEmpresa,
            comprobante: {
              ordenVentaId: f.ordenVentaId,
              clienteRuc: f.clienteRuc,
              clienteRazonSocial: f.clienteRazonSocial,
              tipo: f.tipoComprobante,
              serie: f.serie,
              numero: f.numero,
              fechaEmision: f.fechaEmision,
              fechaVencimiento: f.fechaVencimiento,
              fechaCorte: f.fechaCorte,
              moneda: f.moneda,
              lineas: f.lineas,
              igv: toNum(f.igv),
            },
            usuarioRegistro: usuario?.correo || "sistema",
          });

          completadosIds.push(f.id);
          if (key) clavesRegistradasEnProceso.add(key);
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "COMPLETADO" } : item));
        } catch (err) {
          console.error("Error al registrar venta:", err);
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "ERROR" } : item));
        }
      }

      setTimeout(() => {
        setFacturasPendientes(prev => prev.filter(f => !completadosIds.includes(f.id)));
        if (completadosIds.length > 0) {
          const msgDuplicados = duplicadosDetectados > 0 ? ` ${duplicadosDetectados} duplicado(s) fueron bloqueados.` : "";
          setOk(`Se registraron ${completadosIds.length} comprobantes exitosamente.${msgDuplicados}`);
          recargar();
        } else if (duplicadosDetectados > 0) {
          setError(`No se registró ningún comprobante. Se detectaron ${duplicadosDetectados} duplicado(s).`);
        }
      }, 1500);

    } catch (e) {
      setError("Error en el proceso masivo.");
    } finally {
      setSavingFactura(false);
    }
  };

  const verDetalleAsiento = async ({ asientoId, comprobante }) => {
    if (!asientoId) return;
    try {
      setLoadingAsiento(true);
      const detalle = await obtenerAsientoPorId({ rucEmpresa, asientoId });
      setModalAsiento({
        visible: true,
        data: detalle,
        titulo: `${comprobante?.tipo || ""} ${comprobante?.serie || ""}-${comprobante?.numero || ""}`.trim(),
      });
    } catch (e) { setError("No se pudo cargar el detalle del asiento."); }
    finally { setLoadingAsiento(false); }
  };

  const handleEliminarComprobante = async (x) => {
    // x.id es un ID compuesto, necesitamos el ID real del comprobante
    // En facturasHistorial definimos id como `${x.id}_${c.id}`
    // Pero el c.id es el que necesitamos.
    // Vamos a buscar el comprobante real en el historial.
    const osId = x.osId;
    const os = cxc.find(o => o.id === osId);
    if (!os) return;
    
    const comprobante = (os.comprobantes_relacionados || []).find(c => (c.id === x.comprobanteId) || (`${c.serie}-${c.numero}` === `${x.serie}-${x.numero}`));
    if (!comprobante) {
        setError("No se pudo identificar el comprobante para eliminar.");
        return;
    }

    if (!window.confirm(`¿Está seguro de eliminar el comprobante ${comprobante.serie}-${comprobante.numero}? Esto revertirá el saldo en la OS ${os.os_correlativo} y borrará el asiento contable.`)) return;

    setLoading(true);
    try {
        await eliminarComprobanteVenta({ rucEmpresa, comprobanteId: comprobante.id });
        setOk("Comprobante eliminado y saldo de OS restaurado.");
        await recargar();
    } catch (e) {
        setError(`Error al eliminar: ${e.message}`);
    } finally {
        setLoading(false);
    }
  };

  const handleEditarComprobante = (x) => {
    // x viene de facturasFiltradas, que tiene los datos aplanados.
    // Necesitamos encontrar el comprobante original en cxc para obtener las fechas.
    const os = cxc.find(o => o.id === x.osId);
    if (!os) return;
    const comp = (os.comprobantes_relacionados || []).find(c => c.id === x.comprobanteId || (`${c.serie}-${c.numero}` === `${x.serie}-${x.numero}`));
    
    setEditForm({
        id: x.comprobanteId,
        serie: x.serie || "",
        numero: x.numero || "",
        fechaEmision: x.fechaEmision || "",
        fechaVencimiento: comp?.fecha_vencimiento || x.fechaEmision || "",
        fechaCorte: comp?.fecha_corte || "",
        ordenVentaId: x.osId
    });
    setShowEditModal(true);
  };

  const guardarEdicionComprobante = async () => {
    if (!editForm.id) return;
    setSavingEdit(true);
    try {
        await actualizarComprobanteVenta({
            rucEmpresa,
            comprobanteId: editForm.id,
            comprobante: editForm,
            usuarioRegistro: usuario?.correo || "sistema"
        });
        setOk("Comprobante actualizado correctamente.");
        setShowEditModal(false);
        await recargar();
    } catch (e) {
        setError(`Error al actualizar: ${e.message}`);
    } finally {
        setSavingEdit(false);
    }
  };

  const handleAbrirModalNc = (x) => {
    setNcForm({
      comprobanteId: x?.comprobanteId || x?.id || "",
      serieTarget: x?.serie || "",
      numeroTarget: x?.numero || "",
      comprobanteTexto: `${x?.tipo || "FACTURA"} ${x?.serie || ""}-${x?.numero || ""} (${x?.clienteRazonSocial || ""})`,
      serieNc: "FC01",
      numeroNc: "",
      motivo: "Anulación de la operación por error en facturación"
    });
    setShowNcModal(true);
  };

  const guardarVincularNc = async () => {
    if (!ncForm.serieNc || !ncForm.numeroNc) {
      setError("Ingrese la serie y número de la Nota de Crédito.");
      return;
    }
    setSavingNc(true);
    setError("");
    try {
      await anularComprobanteVentaConNC({
        rucEmpresa,
        comprobanteId: ncForm.comprobanteId,
        serieTarget: ncForm.serieTarget,
        numeroTarget: ncForm.numeroTarget,
        serieNc: ncForm.serieNc,
        numeroNc: ncForm.numeroNc,
        motivo: ncForm.motivo,
        usuarioRegistro: usuario?.correo || "sistema"
      });
      setOk(`Nota de Crédito ${ncForm.serieNc}-${ncForm.numeroNc} vinculada exitosamente. El saldo pendiente pasó a S/ 0.00 y la cuenta del cliente fue desbloqueada.`);
      setShowNcModal(false);
      await recargar();
    } catch (e) {
      setError(`Error al vincular Nota de Crédito: ${e.message}`);
    } finally {
      setSavingNc(false);
    }
  };

  const exportarAsientoPdf = () => {
    const asiento = modalAsiento?.data;
    if (!asiento) return;
    const doc = new jsPDF();
    doc.setFontSize(14); doc.text(`Detalle Asiento ${asiento.id}`, 14, 16);
    autoTable(doc, {
      startY: 25,
      head: [["Cuenta", "Descripción", "Debe", "Haber"]],
      body: [
        ...(asiento.debe || []).map(l => [l.cuenta, l.descripcion, toNum(l.monto).toFixed(2), ""]),
        ...(asiento.haber || []).map(l => [l.cuenta, l.descripcion, "", toNum(l.monto).toFixed(2)]),
        ["", "TOTALES", toNum(asiento.total_debe).toFixed(2), toNum(asiento.total_haber).toFixed(2)]
      ],
      headStyles: { fillColor: [37, 99, 235] }
    });
    doc.save(`asiento_${asiento.id}.pdf`);
  };

  return (
    <div className="p-3">
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="rounded-pill px-4 shadow-sm border-0">{error}</Alert>}
      {ok && <Alert variant="success" onClose={() => setOk("")} dismissible className="rounded-pill px-4 shadow-sm border-0">{ok}</Alert>}

      {showUploadModal && (
        <UploadFacturaExtractor
          onFacturaExtraida={handleFacturasExtraidas}
          onCancelar={() => setShowUploadModal(false)}
        />
      )}

      {/* Cola de Registro Masivo */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <Card.Header 
          className="text-white py-3 d-flex justify-content-between align-items-center" 
          style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)", borderBottom: "3px solid #60a5fa" }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-3 flex-wrap">
            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
              <FaFileInvoiceDollar /> Registro de Ventas por OS (Batch)
              {facturasPendientes.length > 0 && <Badge bg="light" text="dark" className="ms-2 rounded-pill">{facturasPendientes.length}</Badge>}
            </h6>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select
                size="sm"
                value={osSeleccionadaId}
                onChange={(e) => setOsSeleccionadaId(e.target.value)}
                style={{ minWidth: 320, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.95)" }}
              >
                <option value="">Selecciona una OS para facturar...</option>
                {osDisponiblesParaFacturar.map((os) => (
                  <option key={os.id} value={os.id}>
                    {os.os_correlativo} · {os.cliente_razon_social} · Saldo por facturar: {os.moneda} {aMonto(os.saldo_por_facturar)}
                  </option>
                ))}
              </Form.Select>

              <Button 
                size="sm" 
                className="rounded-pill px-3" 
                style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", color: "white" }}
                onClick={() => {
                  if (!osSeleccionadaId) {
                    setError("Primero selecciona una OS para adjuntar facturas.");
                    return;
                  }
                  setShowUploadModal(true);
                }}
              >
                <FaFileUpload className="me-2" /> Cargar (XML/PDF)
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {facturasPendientes.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaCloudUploadAlt size="3rem" className="mb-3 opacity-25" />
              <h5>No hay comprobantes en cola</h5>
              <p className="small mb-0">Arrastra aquí tus facturas o boletas de venta emitidas.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive" style={{ maxHeight: "400px" }}>
                <Table hover className="align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="bg-light text-uppercase small fw-bold">
                    <tr>
                      <th className="px-3">Cliente / Comprobante</th>
                      <th className="px-3">Emisión / Venc.</th>
                      <th className="px-3">Fecha Corte (Bloqueo)</th>
                      <th className="px-3 text-end">Total</th>
                      <th className="px-3 text-center">Estado</th>
                      <th className="px-3 text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturasPendientes.map((f) => (
                      <tr
                        key={f.id}
                        className={
                          f.estado === "ERROR"
                            ? "table-danger"
                            : f.estado === "DUPLICADO"
                              ? "table-warning"
                              : ""
                        }
                      >
                        <td className="px-3">
                          <div className="fw-bold text-dark">{f.clienteRazonSocial}</div>
                          <div className="text-muted extra-small">{f.clienteRuc} | {f.serie}-{f.numero}</div>
                          <div className="text-primary extra-small fw-semibold">OS: {f.osCorrelativo || "-"}</div>
                          {f.lineas && f.lineas.some(l => l.precioUnitarioOs != null && Math.abs(l.precioUnitario - l.precioUnitarioOs) >= 0.01) && (
                            <div className="text-warning extra-small fw-bold mt-1 d-flex align-items-center gap-1">
                              <FaExclamationTriangle className="text-warning" />
                              Precio unitario difiere de la OS (Ref: {f.lineas.map(l => l.precioUnitarioOs != null && Math.abs(l.precioUnitario - l.precioUnitarioOs) >= 0.01 ? `${l.precioUnitarioOs.toFixed(2)} vs ${l.precioUnitario.toFixed(2)}` : null).filter(Boolean).join(", ")})
                            </div>
                          )}
                        </td>
                        <td className="px-3">
                          <div className="small fw-bold">E: {f.fechaEmision}</div>
                          <div className="extra-small text-danger">V: {f.fechaVencimiento}</div>
                        </td>
                        <td className="px-3">
                          <Form.Control
                            type="date"
                            size="sm"
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem', width: '120px' }}
                            value={f.fechaCorte}
                            onChange={(e) => {
                                const val = e.target.value;
                                setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, fechaCorte: val } : item));
                            }}
                          />
                        </td>
                        <td className="px-3 text-end fw-bold">{f.moneda} {aMonto(f.total)}</td>
                        <td className="px-3 text-center">
                          {f.estado === "PENDIENTE" && <Badge bg="secondary">PENDIENTE</Badge>}
                          {f.estado === "PROCESANDO" && <Spinner size="sm" animation="border" variant="primary" />}
                          {f.estado === "DUPLICADO" && <Badge bg="warning" text="dark">DUPLICADO</Badge>}
                          {f.estado === "ERROR" && <Badge bg="danger">ERROR</Badge>}
                        </td>
                        <td className="px-3 text-center">
                          <Button variant="link" className="text-danger p-0" onClick={() => eliminarDeCola(f.id)} disabled={f.estado === "PROCESANDO"}>✕</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="p-3 bg-light border-top d-flex justify-content-end">
                <Button variant="success" className="rounded-pill px-4 shadow-sm" onClick={registrarTodoMasivo} disabled={savingFactura || facturasPendientes.length === 0}>
                  {savingFactura ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheckCircle className="me-2" />} Registrar Todo
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Row className="mb-4 g-3">
        {[
          { label: "Por Cobrar", value: resumen.pendiente, icon: <FaMoneyBillWave />, color: "#1e3a8a", sub: "Saldo CxC total" },
          { label: "Cobrado", value: resumen.cobrado, icon: <FaCheckCircle />, color: "#059669", sub: "Ingresos reales" },
          { label: "Facturado", value: resumen.facturado, icon: <FaFileInvoiceDollar />, color: "#2563eb", sub: "Ventas registradas" }
        ].map((kpi, i) => (
          <Col md={4} key={i}>
            <Card className="border-0 shadow-sm text-white" style={{ borderRadius: "15px", background: kpi.color, overflow: "hidden" }}>
              <Card.Body className="d-flex align-items-center p-3">
                <div style={{ background: "rgba(255,255,255,0.2)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }} className="me-3">{kpi.icon}</div>
                <div>
                  <div className="small opacity-75 text-uppercase fw-bold">{kpi.label}</div>
                  <div className="h4 mb-0 fw-bold">S/ {aMonto(kpi.value)}</div>
                  <div className="extra-small opacity-75">{kpi.sub}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-lg" style={{ borderRadius: "20px", overflow: "hidden" }}>
        <Card.Header
          className="border-0 py-3 px-4"
          style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)" }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h6 className="mb-0 text-white fw-bold">Historial de Cuentas por Cobrar</h6>
            <small className="text-white-50">{facturasFiltradas.length} de {facturasHistorial.length} registro(s)</small>
          </div>
        </Card.Header>

        <Card.Body className="p-3" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)" }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted small">Cargando historial...</span>
            </div>
          ) : facturasHistorial.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaFileInvoiceDollar size="2rem" className="mb-2 opacity-50" />
              <div className="fw-semibold">Sin cuentas por cobrar registradas</div>
              <small>Los comprobantes procesados aparecerán aquí.</small>
            </div>
          ) : (
            <>
              <Row className="mb-3 g-3">
                <Col md={6}>
                  <div className="position-relative">
                    <FaSearch className="position-absolute text-muted" style={{ left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                    <Form.Control
                      type="text"
                      placeholder="Buscar cliente, RUC, OS o comprobante..."
                      value={busquedaHistorial}
                      onChange={(e) => setBusquedaHistorial(e.target.value)}
                      className="ps-5 border-0 shadow-sm"
                      style={{ borderRadius: "12px", background: "#f1f5f9" }}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                    {[
                      { key: "todos", label: "Todos", color: "#64748b" },
                      { key: "pendiente", label: "Pendientes", color: "#f59e0b" },
                      { key: "cobrado", label: "Cobrados", color: "#10b981" },
                      { key: "conAsiento", label: "Con asiento", color: "#2563eb" },
                    ].map(({ key, label, color }) => (
                      <Button
                        key={key}
                        size="sm"
                        className="rounded-pill px-3"
                        style={{
                          background: filtroHistorial === key ? color : "transparent",
                          color: filtroHistorial === key ? "white" : color,
                          border: `2px solid ${color}`,
                          fontWeight: 600,
                        }}
                        onClick={() => setFiltroHistorial(key)}
                      >
                        <FaFilter className="me-1" size={10} /> {label}
                      </Button>
                    ))}
                  </div>
                </Col>
              </Row>

              {facturasFiltradas.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <div className="fw-semibold">No hay resultados con esos filtros</div>
                  <small>Prueba con otra búsqueda o cambia el estado.</small>
                </div>
              ) : (
                <div
                  className="table-responsive"
                  style={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", maxHeight: 460 }}
                >
                  <Table
                    hover
                    className="align-middle mb-0"
                    style={{ fontSize: "0.9rem", borderCollapse: "separate", borderSpacing: 0 }}
                  >
                    <thead style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)" }}>
                      <tr>
                        <th className="text-white py-3 px-3 border-0" style={{ background: "transparent" }}>Cliente</th>
                        <th className="text-white py-3 px-3 border-0" style={{ background: "transparent" }}>Documento</th>
                        <th className="text-white py-3 px-3 border-0" style={{ background: "transparent" }}>Serie / Correlativo</th>
                        <th className="text-white py-3 px-3 border-0 text-center" style={{ background: "transparent" }}>Estado</th>
                        <th className="text-white py-3 px-3 border-0 text-end" style={{ background: "transparent" }}>Total</th>
                        <th className="text-white py-3 px-3 border-0 text-end" style={{ background: "transparent" }}>Saldo</th>
                        <th className="text-white py-3 px-3 border-0 text-center" style={{ background: "transparent" }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facturasFiltradas.map((x, idx) => (
                        <tr
                          key={x.id}
                          style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc", transition: "all 0.2s ease" }}
                          onMouseOver={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
                          onMouseOut={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#f8fafc"; }}
                        >
                          <td className="px-3 py-3 border-0">
                            <div className="fw-bold" style={{ color: "#1e3a8a" }}>{x.clienteRazonSocial || "-"}</div>
                            <div className="extra-small text-muted">{x.clienteRuc || "-"}</div>
                            <div className="extra-small text-primary">OS: {x.osCorrelativo}</div>
                          </td>
                          <td className="px-3 py-3 border-0">
                            <Badge bg="light" text="dark" className="border" style={{ borderRadius: "999px", padding: "6px 12px" }}>
                              {x.tipo || "FACTURA"}
                            </Badge>
                            <div className="extra-small text-muted mt-1">Emisión: {x.fechaEmision || "-"}</div>
                          </td>
                          <td className="px-3 py-3 border-0 text-center">
                            <Badge bg="light" text="dark" className="border" style={{ borderRadius: "999px", padding: "6px 12px" }}>
                              {(x.serie || "-") + "-" + (x.numero || "-")}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 border-0 text-center">
                            <Badge
                              style={{
                                background: x.estadoPago === "PAGADO" ? "#10b981" : x.estadoPago === "CANCELADO_NC" ? "#7c3aed" : "#f59e0b",
                                color: "white",
                                fontWeight: 700,
                                borderRadius: "999px",
                                padding: "6px 12px"
                              }}
                            >
                              {x.estadoPago === "PAGADO" ? "COBRADO" : x.estadoPago === "CANCELADO_NC" ? `NC (${x.notaCreditoRef || "APLICADA"})` : "PENDIENTE"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 border-0 text-end fw-semibold">{x.moneda} {aMonto(x.total)}</td>
                          <td className="px-3 py-3 border-0 text-end fw-bold" style={{ color: "#2563eb" }}>
                            {x.moneda} {aMonto(x.estadoPago === "PAGADO" || x.estadoPago === "CANCELADO_NC" ? 0 : x.total)}
                          </td>
                          <td className="px-3 py-3 border-0 text-center">
                            <div className="d-flex justify-content-center gap-1">
                                {x.asientoId ? (
                                <Button
                                    size="sm"
                                    className="border-0"
                                    style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "999px" }}
                                    title="Ver asiento"
                                    onClick={() => verDetalleAsiento({ asientoId: x.asientoId, comprobante: { tipo: x.tipo, serie: x.serie, numero: x.numero } })}
                                >
                                    <FaEye />
                                </Button>
                                ) : (
                                <span className="text-muted small">Sin asiento</span>
                                )}
                                <Button
                                    size="sm"
                                    className="border-0"
                                    style={{ background: "#e0e7ff", color: "#4338ca", borderRadius: "999px" }}
                                    title="Editar"
                                    onClick={() => handleEditarComprobante(x)}
                                >
                                    <FaEdit />
                                </Button>
                                <Button
                                    size="sm"
                                    className="border-0"
                                    style={{ background: "#fee2e2", color: "#dc2626", borderRadius: "999px" }}
                                    title="Eliminar"
                                    onClick={() => handleEliminarComprobante(x)}
                                >
                                    <FaTrash />
                                </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={modalAsiento.visible} onHide={() => setModalAsiento({ visible: false, data: null, titulo: "" })} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 fw-bold">Detalle de Asiento {modalAsiento?.data?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalAsiento.data && (
            <>
              <Row className="mb-3 small">
                <Col md={12}><strong>Glosa:</strong> {modalAsiento.data.glosa}</Col>
              </Row>
              <Table size="sm" bordered className="extra-small">
                <thead><tr className="bg-light"><th>Cuenta</th><th>Descripción</th><th className="text-end">Debe</th><th className="text-end">Haber</th></tr></thead>
                <tbody>
                  {(modalAsiento.data.debe || []).map((l, i) => <tr key={`d-${i}`}><td>{l.cuenta}</td><td>{l.descripcion}</td><td className="text-end">{aMonto(l.monto)}</td><td></td></tr>)}
                  {(modalAsiento.data.haber || []).map((l, i) => <tr key={`h-${i}`}><td>{l.cuenta}</td><td>{l.descripcion}</td><td></td><td className="text-end">{aMonto(l.monto)}</td></tr>)}
                </tbody>
                <tfoot><tr className="fw-bold bg-light"><td></td><td>TOTALES</td><td className="text-end">{aMonto(modalAsiento.data.total_debe)}</td><td className="text-end">{aMonto(modalAsiento.data.total_haber)}</td></tr></tfoot>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" size="sm" onClick={exportarAsientoPdf}>Exportar PDF</Button>
          <Button variant="secondary" size="sm" onClick={() => setModalAsiento({ visible: false, data: null, titulo: "" })}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Editar Comprobante */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 fw-bold">Editar Comprobante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={6}><Form.Label>Serie</Form.Label><Form.Control value={editForm.serie} onChange={e => setEditForm({...editForm, serie: e.target.value})} /></Col>
              <Col md={6}><Form.Label>Número</Form.Label><Form.Control value={editForm.numero} onChange={e => setEditForm({...editForm, numero: e.target.value})} /></Col>
              <Col md={12}><Form.Label>Fecha Emisión</Form.Label><Form.Control type="date" value={editForm.fechaEmision} onChange={e => setEditForm({...editForm, fechaEmision: e.target.value})} /></Col>
              <Col md={6}><Form.Label>Fecha Vencimiento</Form.Label><Form.Control type="date" value={editForm.fechaVencimiento} onChange={e => setEditForm({...editForm, fechaVencimiento: e.target.value})} /></Col>
              <Col md={6}><Form.Label>Fecha Corte (Bloqueo)</Form.Label><Form.Control type="date" value={editForm.fechaCorte} onChange={e => setEditForm({...editForm, fechaCorte: e.target.value})} /></Col>
            </Row>
            <div className="mt-3 extra-small text-muted">
                <FaExclamationTriangle className="text-warning me-1" /> No se puede cambiar la OS vinculada. Para corregir la OS, elimine el comprobante y vuelva a cargarlo.
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowEditModal(false)}>Cancelar</Button>
          <Button variant="primary" size="sm" onClick={guardarEdicionComprobante} disabled={savingEdit}>
            {savingEdit ? <Spinner size="sm" animation="border" /> : "Guardar Cambios"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Vincular Nota de Crédito (NC) */}
      <Modal show={showNcModal} onHide={() => setShowNcModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 fw-bold d-flex align-items-center gap-2">
            <FaReceipt className="text-purple" /> Vincular Nota de Crédito (NC)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="p-3 mb-3 bg-light rounded-3 border">
              <small className="text-muted d-block">Comprobante a cancelar por NC:</small>
              <div className="fw-bold text-dark">{ncForm.comprobanteTexto}</div>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label className="small fw-semibold">Serie NC</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej. FC01"
                  value={ncForm.serieNc}
                  onChange={(e) => setNcForm({ ...ncForm, serieNc: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-semibold">Número NC</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej. 14"
                  value={ncForm.numeroNc}
                  onChange={(e) => setNcForm({ ...ncForm, numeroNc: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label className="small fw-semibold">Motivo / Glosa de Anulación</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={ncForm.motivo}
                  onChange={(e) => setNcForm({ ...ncForm, motivo: e.target.value })}
                />
              </Col>
            </Row>
            <div className="mt-3 extra-small text-muted border-top pt-2">
              <FaExclamationTriangle className="text-warning me-1" />
              Al vincular la Nota de Crédito, el saldo pendiente de esta factura pasará a <strong>S/ 0.00</strong> y se <strong>desbloqueará automáticamente la cuenta del cliente</strong> conservando el registro histórico para el SIRE.
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0">
          <Button variant="secondary" size="sm" className="rounded-pill px-3" onClick={() => setShowNcModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" className="rounded-pill px-4" onClick={guardarVincularNc} disabled={savingNc}>
            {savingNc ? <Spinner size="sm" animation="border" /> : "Vincular NC y Desbloquear"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionCuentasCobrar;
